import type { BotBundleImportResult } from "@rakazo/contracts";
import {
  BOT_BUNDLE_MEDIA_TYPE,
  BOT_IMPORT_KEY_HEADER,
  BotBundleImportResultSchema,
} from "@rakazo/contracts";
import { withSpaceHeaders } from "./rpc";

/** A refused bundle request; `data` is the server's structured reason, such as a plan limit. */
export class BotBundleRequestError extends Error {
  constructor(
    message: string,
    readonly data?: unknown,
  ) {
    super(message);
    this.name = "BotBundleRequestError";
  }
}

async function failure(response: Response): Promise<BotBundleRequestError> {
  const body: unknown = await response.json().catch(() => null);
  const record = body && typeof body === "object" ? body : {};
  const message = "error" in record && typeof record.error === "string" ? record.error : "";
  return new BotBundleRequestError(message, "data" in record ? record.data : undefined);
}

/** The name the server suggests, preferring the full UTF-8 one. */
export function bundleFileName(disposition: string | null): string | null {
  if (!disposition) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      // Fall through to the plain name.
    }
  }
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? null;
}

/**
 * Downloads a bot's bundle. The server streams it, so the page holds it only as the browser's own
 * file-backed blob until it is saved.
 */
export async function downloadBotBundle(botId: string): Promise<void> {
  const response = await fetch(`/api/bot-export/${encodeURIComponent(botId)}`, {
    credentials: "include",
    headers: withSpaceHeaders(),
  });
  if (!response.ok) throw await failure(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    bundleFileName(response.headers.get("content-disposition")) ?? "bot-export.tar.gz";
  link.click();
  URL.revokeObjectURL(url);
}

// One key per file that has not been imported yet, so choosing the same file again after a lost
// response is a retry the server recognizes instead of a second bot. The form clears its file
// input after every choice, so the next choice is a new File object; a file is told apart by name,
// size and modification time instead. The key is dropped once an import succeeded, because
// importing the same file again then is meant to make another bot.
const importKeys = new Map<string, string>();

function importFingerprint(file: File): string {
  return JSON.stringify([file.name, file.size, file.lastModified]);
}

function importKeyFor(file: File): string {
  const fingerprint = importFingerprint(file);
  const known = importKeys.get(fingerprint);
  if (known) return known;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const key = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  importKeys.set(fingerprint, key);
  return key;
}

/** Uploads a bundle file as the body (the browser streams it from disk) and returns the new bot. */
export async function importBotBundle(file: File): Promise<BotBundleImportResult> {
  const response = await fetch("/api/bot-import", {
    method: "POST",
    credentials: "include",
    headers: withSpaceHeaders({
      "content-type": BOT_BUNDLE_MEDIA_TYPE,
      [BOT_IMPORT_KEY_HEADER]: importKeyFor(file),
    }),
    body: file,
  });
  if (!response.ok) throw await failure(response);
  const imported = BotBundleImportResultSchema.parse(await response.json());
  importKeys.delete(importFingerprint(file));
  return imported;
}
